import React, { createContext, forwardRef, useContext, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DataTable, EmptyState } from "@/legacy/components/backoffice/BackofficeUI";

const ReactApexChart = React.lazy(() => import("react-apexcharts"));

export function Spinner({ label = "Loading" }) {
  return <span className="as-spinner" role="status" aria-label={label} />;
}

export function Button({ children, className = "", ...props }) {
  return <button type="button" className={`btn btn-sm shadow-none ${className}`.trim()} {...props}>{children}</button>;
}

export const Input = forwardRef(function Input(
  { modelValue, onModel, onInput, onChange, state, className = "", ...props },
  ref,
) {
  const handleInput = (event) => {
    onModel?.(props.type === "number" ? event.target.value : event.target.value);
    onInput?.(event.target.value);
  };
  return <input ref={ref} className={`input input-sm ${className} ${state === false ? "input-error is-invalid" : ""}`.trim()} value={modelValue ?? props.value ?? ""} onInput={handleInput} onChange={(event) => onChange?.(event.target.value)} aria-invalid={state === false || undefined} {...props} />;
});

export const Textarea = forwardRef(function Textarea({ modelValue, onModel, onInput, className = "", ...props }, ref) {
  return <textarea ref={ref} className={`textarea ${className}`.trim()} value={modelValue ?? props.value ?? ""} onInput={(event) => { onModel?.(event.target.value); onInput?.(event.target.value); }} {...props} />;
});

export function Select({ options = [], modelValue, value, onModel, onChange, children, className = "", ...props }) {
  const normalized = options.map((option) => typeof option === "object" ? option : { value: option, text: option });
  // value null/undefined diventa "": cosi' resta selezionato il segnaposto disabilitato (value "") invece
  // della prima opzione valida che il browser mostrerebbe da solo, e React non riceve un value nullo.
  return (
    <select className={`select custom-select as-select ${className}`.trim()} value={modelValue ?? value ?? ""} onChange={(event) => { const selected = normalized.find((option) => String(option.value) === event.target.value)?.value ?? event.target.value; onModel?.(selected); onChange?.(selected); }} {...props}>
      {normalized.map((option, index) => <option key={`${option.value}-${index}`} value={option.value ?? ""} disabled={option.disabled}>{option.text ?? option.label ?? option.value}</option>)}
      {children}
    </select>
  );
}

const CheckboxGroupContext = createContext(null);

export function CheckboxGroup({ modelValue = [], onModel, onChange, children, className = "" }) {
  return <div className={className} role="group"><CheckboxGroupContext.Provider value={{ value: modelValue, update(value) { onModel?.(value); onChange?.(value); } }}>{children}</CheckboxGroupContext.Provider></div>;
}

export function Checkbox({ modelValue, onModel, onChange, value = true, disabled, children, className = "", checked }) {
  const group = useContext(CheckboxGroupContext);
  const isArray = Array.isArray(group?.value ?? modelValue);
  const current = group?.value ?? modelValue;
  const isChecked = checked ?? (isArray ? current.includes(value) : Boolean(current));
  return (
    <label className={`as-check ${className}`.trim()}>
      <input type="checkbox" checked={isChecked} disabled={disabled} value={String(value)} onChange={(event) => {
        const next = isArray
          ? event.target.checked ? [...current, value] : current.filter((item) => item !== value)
          : event.target.checked;
        group?.update(next); onModel?.(next); onChange?.(next);
      }} />
      <span>{children}</span>
    </label>
  );
}

export function Radio({ modelValue, onModel, onChange, value, children, className = "", ...props }) {
  const name = useId();
  return <label className={`as-check ${className}`.trim()}><input type="radio" name={name} checked={modelValue === value} onChange={() => { onModel?.(value); onChange?.(value); }} {...props} /><span>{children}</span></label>;
}

const modalListeners = new Map();
export const modalService = {
  show(id) { modalListeners.get(id)?.(true); },
  hide(id) { modalListeners.get(id)?.(false); },
};

export function Modal({ id, visible, modelValue, onModel, onHide, onShow, onHidden, children, className = "", size }) {
  const controlled = visible !== undefined || modelValue !== undefined;
  const [internalOpen, setInternalOpen] = useState(Boolean(visible ?? modelValue));
  const open = controlled ? Boolean(visible ?? modelValue) : internalOpen;
  const panelRef = useRef(null);
  const previousFocus = useRef(null);

  const setOpen = (next) => {
    if (!next) onHide?.(); else onShow?.();
    if (!controlled) setInternalOpen(next);
    onModel?.(next);
    if (!next) onHidden?.();
  };
  useEffect(() => { if (id) { modalListeners.set(id, setOpen); return () => modalListeners.delete(id); } }, [id, controlled]);
  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = document.activeElement;
    requestAnimationFrame(() => panelRef.current?.focus());
    const keydown = (event) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key === "Tab") {
        const focusable = [...panelRef.current.querySelectorAll('button,a,input,select,textarea,[tabindex]:not([tabindex="-1"])')];
        if (!focusable.length) return;
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); previousFocus.current?.focus?.(); };
  }, [open]);
  if (!open) return null;
  const portalHost = document.getElementById("modal-root") || document.body;
  return createPortal(<div className="modal modal-open as-modal-backdrop dw-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}><section id={id} ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" className={`modal-box modal-dialog dw-modal-dialog border border-base-300 bg-base-100 p-0 shadow-overlay ${size === "lg" ? "modal-lg max-w-4xl" : ""} ${className}`.trim()}>{children}</section></div>, portalHost);
}

let toastSequence = 0;
const toastSubscribers = new Set();
let toasts = [];
function publishToasts() { toastSubscribers.forEach((listener) => listener([...toasts])); }
export const toastService = {
  toast(message, options = {}) {
    const id = ++toastSequence;
    toasts = [...toasts, { id, message, ...options }]; publishToasts();
    setTimeout(() => { toasts = toasts.filter((toast) => toast.id !== id); publishToasts(); }, options.autoHideDelay || 4500);
  },
  show(id) { document.getElementById(id)?.removeAttribute("hidden"); },
  hide(id) { document.getElementById(id)?.setAttribute("hidden", ""); },
};

export function ToastViewport() {
  const [items, setItems] = useState(toasts);
  useEffect(() => { toastSubscribers.add(setItems); return () => toastSubscribers.delete(setItems); }, []);
  return <div className="as-toast-viewport" aria-live="polite">{items.map((toast) => <div className={`as-toast ${toast.variant || "info"}`} key={toast.id}>{toast.title && <strong>{toast.title}</strong>}<span>{toast.message}</span></div>)}</div>;
}

export function StaticToast({ id, children, className = "", variant = "info" }) {
  return <div id={id} role="status" hidden className={`alert alert-${variant === "danger" ? "error" : variant} as-inline-toast ${variant} ${className}`.trim()}>{children}</div>;
}

export const PerfectScrollbar = forwardRef(function PerfectScrollbar({ children, onPsYReachEnd, className = "", ...props }, ref) {
  return <div ref={ref} className={`as-scroll ${className}`.trim()} onScroll={(event) => { const node = event.currentTarget; if (node.scrollTop + node.clientHeight >= node.scrollHeight - 2) onPsYReachEnd?.(); }} {...props}>{children}</div>;
});

export function Collapse({ id, modelValue, visible, onModel: _onModel, children, className = "", accordion: _accordion, ...props }) {
  const controlled = modelValue !== undefined || visible !== undefined;
  const [internalShown, setInternalShown] = useState(Boolean(visible));
  const shown = controlled ? Boolean(modelValue ?? visible) : internalShown;
  useEffect(() => {
    if (!id || controlled) return undefined;
    const toggle = (event) => { if (event.detail === id) setInternalShown((value) => !value); };
    document.addEventListener("as-toggle-collapse", toggle);
    return () => document.removeEventListener("as-toggle-collapse", toggle);
  }, [id, controlled]);
  return <div id={id} className={`as-collapse ${shown ? "show" : ""} ${className}`.trim()} hidden={!shown} {...props}>{children}</div>;
}

export function Progress({ value = 0, max = 100, children, className = "" }) {
  return <div className={`as-progress ${className}`.trim()} role="progressbar" aria-valuenow={value} aria-valuemax={max}>{children || <span style={{ width: `${Math.min(100, value / max * 100)}%` }} />}</div>;
}

export function Table({ items = [], fields = [], scopedSlots = {}, className = "" }) {
  const normalizedFields = fields.map((field) => typeof field === "string" ? { key: field, label: field } : field);
  return <DataTable className="backoffice-list-table"><table className={`table table-sm w-full ${className}`.trim()}><thead><tr>{normalizedFields.map((field) => { const slot = scopedSlots[`head(${field.key})`]; return <th className={field.key === "actions" || field.key === "option" ? "text-right" : ""} key={field.key}>{slot ? slot({ field, label: field.label }) : field.label}</th>; })}</tr></thead><tbody>{items.map((item, rowIndex) => <tr className="hover:bg-base-200" key={item.id ?? rowIndex}>{normalizedFields.map((field) => { const slot = scopedSlots[`cell(${field.key})`]; return <td className={field.key === "actions" || field.key === "option" ? "text-right" : ""} key={field.key}>{slot ? slot({ item, value: item[field.key], index: rowIndex }) : String(item[field.key] ?? "")}</td>; })}</tr>)}</tbody></table>{items.length === 0 && <EmptyState compact title="No results" description="There are no items matching the current filters." />}</DataTable>;
}

export function Datepicker({ modelValue = {}, onModel, onChange, className = "", timePicker = false }) {
  const type = timePicker ? "datetime-local" : "date";
  const format = (value) => value ? new Date(value).toISOString().slice(0, timePicker ? 16 : 10) : "";
  return <div className={`as-date-range ${className}`.trim()}><input type={type} aria-label="Start date" value={format(modelValue.startDate)} onChange={(event) => { const next = { ...modelValue, startDate: new Date(event.target.value) }; onModel?.(next); onChange?.(next); }} /><span>–</span><input type={type} aria-label="End date" value={format(modelValue.endDate)} onChange={(event) => { const next = { ...modelValue, endDate: new Date(event.target.value) }; onModel?.(next); onChange?.(next); }} /></div>;
}

export function Toggle({ modelValue, value, onModel, onChange, className = "", disabled }) {
  const checked = Boolean(modelValue ?? value);
  return <button type="button" role="switch" aria-checked={checked} disabled={disabled} className={`as-toggle ${checked ? "on" : ""} ${className}`.trim()} onClick={() => { onModel?.(!checked); onChange?.({ value: !checked }); }}><span /></button>;
}

export function Slider({ modelValue, onModel, onChange, min = 0, max = 100, interval = 1, className = "" }) {
  const values = Array.isArray(modelValue) ? modelValue : [modelValue ?? min];
  return <div className={`as-slider ${className}`.trim()}>{values.map((value, index) => <input key={index} type="range" min={min} max={max} step={interval} value={value} onChange={(event) => { const next = Array.isArray(modelValue) ? values.map((item, itemIndex) => itemIndex === index ? Number(event.target.value) : item) : Number(event.target.value); onModel?.(next); onChange?.(next); }} />)}</div>;
}

export function ColorPicker({ modelValue, onModel, onInput, className = "" }) {
  const color = typeof modelValue === "string" ? modelValue : modelValue?.hex || "#398ff4";
  return <input className={className} type="color" value={color} onChange={(event) => { const next = { hex: event.target.value, hex8: `${event.target.value}ff` }; onModel?.(next); onInput?.(next); }} />;
}

export function Draggable({ modelValue = [], onModel, children, className = "" }) {
  const sourceIndex = useRef(null);
  const childArray = React.Children.toArray(children);
  return <div className={className}>{childArray.map((child, index) => <div key={child.key ?? index} draggable onDragStart={() => { sourceIndex.current = index; }} onDragOver={(event) => event.preventDefault()} onDrop={() => { if (sourceIndex.current === null || sourceIndex.current === index) return; const next = [...modelValue]; const [moved] = next.splice(sourceIndex.current, 1); next.splice(index, 0, moved); sourceIndex.current = null; onModel?.(next); }}>{child}</div>)}</div>;
}

export function ApexChart({ options = {}, ...props }) {
  const themedOptions = {
    ...options,
    chart: { fontFamily: "Public Sans, sans-serif", foreColor: "#72828f", background: "transparent", toolbar: { show: false, ...(options.chart?.toolbar || {}) }, ...options.chart },
    grid: { borderColor: "#edf0f5", strokeDashArray: 3, padding: { left: 8, right: 8 }, ...options.grid },
    legend: { fontSize: "11px", fontWeight: 500, markers: { width: 8, height: 8, radius: 4 }, itemMargin: { horizontal: 10, vertical: 4 }, ...options.legend },
    tooltip: { theme: "light", ...options.tooltip },
    noData: { text: "No data for this period", align: "center", verticalAlign: "middle", style: { color: "#72828f", fontSize: "12px" }, ...options.noData },
  };
  return <div className="as-apex-chart min-w-0 w-full"><React.Suspense fallback={<Spinner label="Loading chart" />}><ReactApexChart width="100%" options={themedOptions} {...props} /></React.Suspense></div>;
}
